import React from 'react';
import { Users, Calendar, Zap } from 'lucide-react';

const NotificationsSection = ({
  t,
  notificationsData,
  setNotificationsData,
  hrUsers,
  toggleUserNotification,
  toggleAppointmentUser,
  toggleAnalysisUser
}) => {
  const updateField = (field, value) => {
    setNotificationsData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderUserChecklist = (selectedIds, onToggle) => {
    if (!hrUsers || hrUsers.length === 0) {
      return (
        <p className="text-xs text-gray-500">{t('settings.notifications.noHrUsers')}</p>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {hrUsers.map(user => (
          <label key={user.id} className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={selectedIds.includes(user.id)}
              onChange={() => onToggle(user.id)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{user.name || user.email}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Applications */}
      <div className="settings-notification-card">
        <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
          <Users className="w-5 h-5 mr-2 text-blue-600" />
          {t('settings.notifications.applications.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('settings.notifications.applications.description')}
        </p>

        <div className="space-y-4">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.application_notifications_enabled}
              onChange={(e) => updateField('application_notifications_enabled', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.applications.enabled')}</span>
          </label>

          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.default_notify_publisher}
              onChange={(e) => updateField('default_notify_publisher', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.applications.notifyPublisher')}</span>
          </label>
          <p className="text-xs text-gray-500">
            {t('settings.notifications.applications.notifyPublisherNote')}
          </p>

          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">
              {t('settings.notifications.applications.customUsers')}
            </p>
            <p className="text-xs text-gray-500 mb-2">
              {t('settings.notifications.applications.customUsersNote')}
            </p>
            {renderUserChecklist(notificationsData.custom_notification_users, toggleUserNotification)}
          </div>
        </div>
      </div>

      {/* Appointments */}
      <div className="settings-notification-card">
        <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-green-600" />
          {t('settings.notifications.appointments.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('settings.notifications.appointments.description')}
        </p>

        <div className="space-y-4">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.appointment_notifications_enabled}
              onChange={(e) => updateField('appointment_notifications_enabled', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.appointments.enabled')}</span>
          </label>

          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.appointment_notify_recruiter}
              onChange={(e) => updateField('appointment_notify_recruiter', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.appointments.notifyRecruiter')}</span>
          </label>
          <p className="text-xs text-gray-500">
            {t('settings.notifications.appointments.notifyRecruiterNote')}
          </p>

          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">
              {t('settings.notifications.appointments.customUsers')}
            </p>
            <p className="text-xs text-gray-500 mb-2">
              {t('settings.notifications.appointments.customUsersNote')}
            </p>
            {renderUserChecklist(notificationsData.appointment_custom_users, toggleAppointmentUser)}
          </div>
        </div>
      </div>

      {/* Analyses */}
      <div className="settings-notification-card">
        <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-purple-600" />
          {t('settings.notifications.analyses.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('settings.notifications.analyses.description')}
        </p>

        <div className="space-y-4">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.analysis_notifications_enabled}
              onChange={(e) => updateField('analysis_notifications_enabled', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.analyses.enabled')}</span>
          </label>

          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notificationsData.analysis_notify_executor}
              onChange={(e) => updateField('analysis_notify_executor', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t('settings.notifications.analyses.notifyExecutor')}</span>
          </label>
          <p className="text-xs text-gray-500">
            {t('settings.notifications.analyses.notifyExecutorNote')}
          </p>

          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">
              {t('settings.notifications.analyses.customUsers')}
            </p>
            <p className="text-xs text-gray-500 mb-2">
              {t('settings.notifications.analyses.customUsersNote')}
            </p>
            {renderUserChecklist(notificationsData.analysis_custom_users, toggleAnalysisUser)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSection;
